import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import { LoaderScreen } from "@/components/common/LoaderScreen";
import { Button } from "@/components/ui/button";
import { getOidcConfig } from "@/config/auth";
import { isPublicRoute } from "@/config/routes";
import { PAGE_PATHS } from "@/config/urls";
import { useAuthFlow } from "@/hooks/useAuthFlow";
import { userQueryKeys } from "@/hooks/userQueryKeys";
import { useSessionRefreshManager } from "@/hooks/useSessionRefreshManager";
import { useUser } from "@/hooks/useUser";
import { rememberAuthenticatedUserId } from "@/lib/authenticatedUserIdentity";
import {
  clearAuthSessionState,
  getHumhubExpiresAt,
  getLatestOidcToken,
} from "@/lib/authSession";
import { setAuthFlowHandler } from "@/lib/axios";
import { authApi } from "@/lib/humhub-api/auth";
import type { User } from "@/lib/humhub-api/types";
import { getAuthReturnUrl } from "@/lib/navigation/authReturnUrl";
import * as TokenUtils from "@/lib/TokenManager";

interface AuthContextType {
  isAuthReady: boolean;
  retry: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthReady: false,
  retry: () => {},
});

export const useAuthContext = () => useContext(AuthContext);

// TanStack Query ベースのユーザー情報取得フック
export const useGlobalUserInfo = (): User | null => {
  const shouldFetchCurrentUser =
    typeof window === "undefined"
      ? true
      : !isPublicRoute(window.location.pathname);
  const { currentUser } = useUser({ enabled: shouldFetchCurrentUser });
  useEffect(() => {
    if (typeof currentUser?.id === "number") {
      rememberAuthenticatedUserId(currentUser.id);
    }
  }, [currentUser?.id]);
  return currentUser || null;
};

// ユーザー情報を更新する統一関数
export const updateGlobalUserInfo = (
  queryClient: ReturnType<typeof useQueryClient>,
  userInfo: User | null,
) => {
  if (userInfo) {
    rememberAuthenticatedUserId(userInfo.id);
    // TanStack Query のキャッシュを更新
    queryClient.setQueryData(userQueryKeys.current(), userInfo);
    if (userInfo.id) {
      queryClient.setQueryData(userQueryKeys.byId(userInfo.id), userInfo);
    }
  } else {
    // ユーザー情報をクリア
    queryClient.setQueryData(userQueryKeys.current(), null);
    queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
  }
};

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * OIDC関連のブラウザストレージデータをクリーンアップ
 */
const cleanupOidcStorage = () => {
  try {
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach((key) => {
      if (key.includes("oidc.")) {
        sessionStorage.removeItem(key);
      }
    });

    const localKeys = Object.keys(localStorage);
    localKeys.forEach((key) => {
      if (key.includes("oidc.")) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error("❌ Error during OIDC storage cleanup:", error);
  }
};

/**
 * 認証状態管理コンポーネント
 */
export const AuthStateManager: React.FC<{
  children: React.ReactNode;
  setIsAuthReady: (ready: boolean) => void;
  setRetryFunction?: (retry: () => void) => void;
}> = ({ children, setIsAuthReady, setRetryFunction }) => {
  const auth = useAuth();
  const authFlow = useAuthFlow();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  /**
   * 元のURLにリダイレクト
   */
  const redirectToOriginalUrl = useCallback(() => {
    try {
      const originalUrl = TokenUtils.getOriginalUrl();
      if (originalUrl) {
        TokenUtils.clearOriginalUrl();
        const url = new URL(originalUrl);
        if (url.pathname === PAGE_PATHS.CALLBACK) {
          window.location.replace("/");
          return;
        }

        window.location.replace(
          getAuthReturnUrl(originalUrl, window.location.origin),
        );
      } else {
        window.location.replace("/");
      }
    } catch (_error) {
      window.location.replace("/");
    }
  }, []);

  // 状態管理（ref使用で再レンダリングを防ぐ）
  const isInitialized = useRef(false);
  const isProcessing = useRef(false);
  const hasCheckedAuth = useRef(false);
  const hasCleanedOidcStorage = useRef(false);
  const hasScheduledCallbackRedirect = useRef(false);
  const bootstrapRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const bootstrapRetryAttempt = useRef(0);
  const checkAuthStateRef = useRef<(() => Promise<void>) | null>(null);

  const scheduleCallbackRedirect = useCallback(() => {
    if (hasScheduledCallbackRedirect.current) {
      return;
    }

    hasScheduledCallbackRedirect.current = true;
    setTimeout(() => {
      redirectToOriginalUrl();
    }, 80);
  }, [redirectToOriginalUrl]);

  // UI状態のみstateで管理
  const [showLoading, setShowLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<
    "transient" | "protocol" | null
  >(null);

  const clearBootstrapRetry = useCallback(() => {
    if (bootstrapRetryTimer.current === null) return;
    clearTimeout(bootstrapRetryTimer.current);
    bootstrapRetryTimer.current = null;
  }, []);

  const completeBootstrap = useCallback(() => {
    clearBootstrapRetry();
    bootstrapRetryAttempt.current = 0;
    setBootstrapError(null);
    setIsAuthReady(true);
    setShowLoading(false);
    hasCheckedAuth.current = true;
  }, [clearBootstrapRetry, setIsAuthReady]);

  const scheduleBootstrapRetry = useCallback(() => {
    const delays = [30_000, 60_000, 120_000, 300_000] as const;
    if (
      bootstrapRetryTimer.current !== null ||
      bootstrapRetryAttempt.current >= delays.length ||
      navigator.onLine === false
    ) {
      return;
    }

    const baseDelay = delays[bootstrapRetryAttempt.current];
    const jitter = Math.floor(baseDelay * 0.2 * Math.random());
    bootstrapRetryAttempt.current += 1;
    bootstrapRetryTimer.current = setTimeout(
      () => {
        bootstrapRetryTimer.current = null;
        void checkAuthStateRef.current?.();
      },
      Math.min(300_000, baseDelay + jitter),
    );
  }, []);

  const retryBootstrapAuth = useCallback(() => {
    clearBootstrapRetry();
    bootstrapRetryAttempt.current = 0;
    hasCheckedAuth.current = false;
    setBootstrapError(null);
    setShowLoading(true);
    void checkAuthStateRef.current?.();
  }, [clearBootstrapRetry]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      clearAuthSessionState();
    }
  }, [auth.isAuthenticated]);

  useSessionRefreshManager({
    enabled:
      !auth.isLoading &&
      isInitialized.current &&
      hasCheckedAuth.current &&
      Boolean(
        auth.isAuthenticated || getLatestOidcToken() || getHumhubExpiresAt(),
      ),
  });

  /**
   * 認証状態をチェック（TanStack Query統合版）
   */
  const checkAuthState = useCallback(async () => {
    if (isProcessing.current || hasCheckedAuth.current) {
      return;
    }

    // コールバックURL上では、OIDC側の処理完了を待ってから元の画面へ戻す
    const isCallbackUrl = window.location.pathname === PAGE_PATHS.CALLBACK;
    if (isCallbackUrl) {
      // react-oidc-context の処理が完了する前にリダイレクトするとループするため、ロード終わりを待つ
      if (auth.isLoading || auth.activeNavigator) {
        return;
      }

      hasCheckedAuth.current = true;
      scheduleCallbackRedirect();
      return;
    }

    isProcessing.current = true;

    try {
      // HumHubトークンの有効性をAPIリクエストで検証
      try {
        const currentUser = await authApi.getCurrentUserOrNull();
        if (currentUser) {
          // TanStack Query のキャッシュに保存（グローバル状態の代替）
          updateGlobalUserInfo(queryClient, currentUser);

          // OIDC認証が完了した場合はストレージをクリーンアップ
          if (!hasCleanedOidcStorage.current) {
            cleanupOidcStorage();
            hasCleanedOidcStorage.current = true;
          }

          completeBootstrap();
          return;
        }

        clearAuthSessionState();
        updateGlobalUserInfo(queryClient, null);
      } catch (error) {
        console.log("⚠️ HumHub token validation failed:", error);
        // トークンが無効な場合はユーザー情報をクリア
        clearAuthSessionState();
        updateGlobalUserInfo(queryClient, null);
      }

      // 認証フロー実行
      const result = await authFlow.executeAuthFlow();

      if (result.status === "recovered") {
        // 認証成功後にユーザー情報を再取得
        try {
          const currentUser = await authApi.getCurrentUser();
          if (currentUser) {
            updateGlobalUserInfo(queryClient, currentUser);
          }
        } catch (error) {
          console.error(
            "❌ Failed to get user info after auth success:",
            error,
          );
        }

        // 認証成功時もストレージをクリーンアップ
        if (!hasCleanedOidcStorage.current) {
          cleanupOidcStorage();
          hasCleanedOidcStorage.current = true;
        }

        completeBootstrap();
      } else if (
        result.status === "reauth_required" &&
        result.reason === "invalid_refresh_token"
      ) {
        console.log("🔄 Authentication redirecting...");
        setBootstrapError(null);
        setShowLoading(true);
      } else if (result.status === "retry_later") {
        console.log("❌ Authentication recovery unavailable:", result.reason);
        setBootstrapError("transient");
        setShowLoading(false);
        scheduleBootstrapRetry();
      } else {
        console.log("❌ Authentication recovery unavailable:", result.reason);
        clearBootstrapRetry();
        setBootstrapError("protocol");
        setShowLoading(false);
      }
    } catch (error) {
      console.error("❌ Auth state check failed:", error);
      clearAuthSessionState();
      updateGlobalUserInfo(queryClient, null);
      setBootstrapError("transient");
      setShowLoading(false);
      scheduleBootstrapRetry();
    } finally {
      isProcessing.current = false;
    }
  }, [
    authFlow.executeAuthFlow,
    queryClient,
    auth.isLoading,
    auth.activeNavigator, // コールバック中を正しく検知するため依存に含める
    clearBootstrapRetry,
    completeBootstrap,
    scheduleCallbackRedirect,
    scheduleBootstrapRetry,
  ]);

  checkAuthStateRef.current = checkAuthState;

  useEffect(() => {
    setRetryFunction?.(retryBootstrapAuth);
    return () => setRetryFunction?.(() => {});
  }, [retryBootstrapAuth, setRetryFunction]);

  useEffect(() => {
    const handleOnline = () => {
      if (bootstrapError !== "transient" || hasCheckedAuth.current) return;
      clearBootstrapRetry();
      void checkAuthStateRef.current?.();
    };
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [bootstrapError, clearBootstrapRetry]);

  useEffect(() => () => clearBootstrapRetry(), [clearBootstrapRetry]);

  // 初期化処理（一度だけ実行）
  useEffect(() => {
    if (!isInitialized.current) {
      setAuthFlowHandler(authFlow);
      isInitialized.current = true;
      checkAuthState();
    }
  }, [authFlow, checkAuthState]);

  // 使用済み/期限切れのcallback URLを再訪した場合は、認証中表示から抜ける
  useEffect(() => {
    if (
      !isInitialized.current ||
      hasCheckedAuth.current ||
      isProcessing.current
    ) {
      return;
    }

    const isCallbackUrl = window.location.pathname === PAGE_PATHS.CALLBACK;
    if (!isCallbackUrl || auth.error?.source !== "signinCallback") {
      return;
    }

    clearAuthSessionState();
    updateGlobalUserInfo(queryClient, null);
    hasCheckedAuth.current = true;
    window.history.replaceState({}, document.title, PAGE_PATHS.CALLBACK);
    scheduleCallbackRedirect();
  }, [auth.error, queryClient, scheduleCallbackRedirect]);

  // コールバックURLでは、OIDC処理が成功/失敗どちらで完了しても認証チェックを再開する
  useEffect(() => {
    if (
      !isInitialized.current ||
      hasCheckedAuth.current ||
      isProcessing.current
    ) {
      return;
    }

    const isCallbackUrl = window.location.pathname === PAGE_PATHS.CALLBACK;
    if (isCallbackUrl && !auth.isLoading && !auth.activeNavigator) {
      checkAuthState();
    }
  }, [auth.isLoading, auth.activeNavigator, checkAuthState]);

  // OIDC認証状態の変化を監視
  useEffect(() => {
    const handleAuthChange = async () => {
      if (
        isInitialized.current &&
        !hasCheckedAuth.current &&
        !isProcessing.current
      ) {
        if (auth.isAuthenticated && auth.user?.access_token) {
          if (!hasCleanedOidcStorage.current) {
            cleanupOidcStorage();
            hasCleanedOidcStorage.current = true;
          }

          setTimeout(() => {
            checkAuthState();
          }, 500);
        }
      }
    };
    handleAuthChange();
  }, [auth.isAuthenticated, auth.user?.access_token, checkAuthState]);

  if (bootstrapError) {
    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
      >
        <p>
          {bootstrapError === "protocol"
            ? t("common.authProtocolError")
            : t("common.authConnectionError")}
        </p>
        <Button type="button" onClick={retryBootstrapAuth}>
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  // ローディング画面
  if (showLoading || authFlow.isAuthenticating) {
    return <LoaderScreen />;
  }

  return <>{children}</>;
};

/**
 * 認証プロバイダーコンポーネント
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [retryFunction, setRetryFunction] = useState<() => void>(
    () => () => {},
  );
  const registerRetryFunction = useCallback((retry: () => void) => {
    setRetryFunction(() => retry);
  }, []);
  const oidcConfig = getOidcConfig();

  const contextValue = {
    isAuthReady,
    retry: retryFunction,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <OidcAuthProvider {...oidcConfig}>
        <AuthStateManager
          setIsAuthReady={setIsAuthReady}
          setRetryFunction={registerRetryFunction}
        >
          {isAuthReady ? children : <LoaderScreen />}
        </AuthStateManager>
      </OidcAuthProvider>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
